import {
  TemplateLayoutTypeEnum,
  TemplateNodeSchema,
} from '@ulysses-inc/harami_api_client'
import { type ReactNode, RefObject } from 'react'
import {
  ConnectDragPreview,
  ConnectDragSource,
  DropTargetMonitor,
  useDrag,
  useDrop,
} from 'react-dnd'
import { useDispatch } from 'react-redux'
import { TemplateNodesDict } from 'src/exShared/types/types'
import {
  DragItem,
  HoveringPosition,
  MoveNodeEvent,
} from 'src/features/templateEdit/dragAndDrop/DnDTemplateType'
import { DnDUtils } from 'src/features/templateEdit/dragAndDrop/DnDUtils'
import { moveTemplateNode } from 'src/state/ducks/templates/actions'
import { ERROR_MESSAGES } from '../common/errorMessages'
import { useErrorDialog } from '../common/useErrorDialog'
import { shouldBlockTableMoveIntoEmployeeCheck } from '../section/table/model'
import { useGridLayoutSoftLimitErrorLog } from '../useSoftLimitErrorLog'
import { validateLogicNestCountOnDraggingQuestion } from '../validates/validate'
import { TReactState, checkIsEmployeeCheck } from './Question.utils'

type UseDragAndDropParamTemplateInfo = {
  layoutType: TemplateLayoutTypeEnum
  templateNodes: TemplateNodesDict
}

type UseDragAndDropParam = {
  hoverPositionState: TReactState<HoveringPosition>
  parentNode: TemplateNodeSchema | null
  node: TemplateNodeSchema
  templateInfo: UseDragAndDropParamTemplateInfo
  ref: RefObject<HTMLDivElement>
}

export const useDragAndDrop = ({
  hoverPositionState: [hoverPosition, setHoverPosition],
  parentNode,
  node,
  templateInfo: { layoutType, templateNodes },
  ref,
}: UseDragAndDropParam): {
  isOver: boolean
  isDragging: boolean
  drag: ConnectDragSource
  preview: ConnectDragPreview
  errorDialog: ReactNode
} => {
  const { logSoftLimitErrorEvent } = useGridLayoutSoftLimitErrorLog('dragNode')
  const dispatch = useDispatch()
  const { showError, errorDialog } = useErrorDialog()

  const moveNode = (event: MoveNodeEvent) => {
    const { dragNode, dragNodeParent, dropNodeParent } = event
    const isSameParent = !!(
      (!dragNodeParent && !dropNodeParent) ||
      (dragNodeParent &&
        dropNodeParent &&
        dragNodeParent.id === dropNodeParent.id)
    )
    const errorMsg = validateLogicNestCountOnDraggingQuestion(
      layoutType,
      dragNode,
      dropNodeParent,
      templateNodes,
    )

    if (errorMsg) {
      showError(ERROR_MESSAGES.LOGIC_NEST_LIMIT.title, errorMsg)
      logSoftLimitErrorEvent('logicNestCount', errorMsg)
    } else {
      dispatch(moveTemplateNode(event, isSameParent))
    }
  }

  const [{ isOver }, drop] = useDrop({
    accept: 'NODE',
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
    hover: (_: DragItem, monitor: DropTargetMonitor) => {
      if (checkIsEmployeeCheck(parentNode, node)) {
        setHoverPosition(undefined)
        return
      }
      if (monitor.isOver({ shallow: true })) {
        const position = DnDUtils.getHoveringPosition(ref, monitor)
        if (hoverPosition !== position) {
          setHoverPosition(position)
        }
      } else {
        setHoverPosition(undefined)
      }
    },
    drop: ({ node: dragNode, parentNode: dragNodeParent }: DragItem) => {
      if (!hoverPosition) {
        return
      }
      if (dragNode.uuid === node.uuid) {
        return
      }
      if (checkIsEmployeeCheck(parentNode, node)) {
        return
      }
      // 表（またはそれを含むセクション）を従業員チェック内に移動させない
      if (
        shouldBlockTableMoveIntoEmployeeCheck(
          dragNode,
          parentNode,
          templateNodes,
        )
      ) {
        showError(
          ERROR_MESSAGES.TABLE_MOVE_TO_EMPLOYEE_CHECK.title,
          ERROR_MESSAGES.TABLE_MOVE_TO_EMPLOYEE_CHECK.message,
        )
        return
      }

      const event: MoveNodeEvent = {
        dragNode,
        dropNode: node,
        dropPosition: hoverPosition,
        dragNodeParent,
        dropNodeParent: parentNode,
      }
      moveNode(event)
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'NODE',
    item: { node, parentNode },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drop(ref)

  return {
    isOver,
    isDragging,
    drag,
    preview,
    errorDialog,
  }
}
